import type { ConversationSummary, PlanSummary } from '@knownow/domain'
import { supabase } from './supabase'

const AGENT_URL =
  (import.meta.env['VITE_AGENT_URL'] as string | undefined) ?? 'http://127.0.0.1:3001'

async function getAuthHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) {
    throw new Error('Not authenticated')
  }
  return { Authorization: `Bearer ${token}` }
}

function parseConversationSummary(raw: unknown): ConversationSummary {
  const obj = raw as ConversationSummary
  return {
    ...obj,
    generatedAt: new Date(obj.generatedAt),
  }
}

function parsePlanSummary(raw: unknown): PlanSummary {
  const obj = raw as PlanSummary
  return {
    ...obj,
    generatedAt: new Date(obj.generatedAt),
  }
}

interface RequestOptions {
  /** Treat a 404 response as "not found yet" rather than an error. Only set
   *  on GET — for POST (generate) a 404 means the route is unreachable and
   *  should surface as an error. */
  readonly nullOn404?: boolean
}

async function extractErrorMessage(response: Response): Promise<string> {
  let text = ''
  try {
    text = await response.text()
  } catch {
    // body unreadable — fall through to statusText
  }
  if (!text) return response.statusText || 'Unknown error'
  try {
    const parsed = JSON.parse(text) as { error?: unknown; message?: unknown }
    if (typeof parsed.error === 'string') return parsed.error
    if (typeof parsed.message === 'string') return parsed.message
  } catch {
    // body isn't JSON — fall through to raw text
  }
  return text
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  parse: (raw: unknown) => T,
  opts: RequestOptions = {},
): Promise<T | null> {
  const headers = await getAuthHeader()
  const response = await fetch(`${AGENT_URL}${path}`, { method, headers })
  if (response.status === 404 && opts.nullOn404) return null
  if (!response.ok) {
    const message = await extractErrorMessage(response)
    throw new Error(`${response.status} ${message}`)
  }
  const body = (await response.json()) as unknown
  return parse(body)
}

export async function getConversationSummary(
  conversationId: string,
): Promise<ConversationSummary | null> {
  return request(
    'GET',
    `/api/conversations/${conversationId}/summary`,
    parseConversationSummary,
    { nullOn404: true },
  )
}

export async function generateConversationSummary(
  conversationId: string,
): Promise<ConversationSummary> {
  const result = await request(
    'POST',
    `/api/conversations/${conversationId}/summary`,
    parseConversationSummary,
  )
  if (result === null) {
    throw new Error('Summary generation failed')
  }
  return result
}

export async function getPlanSummary(planId: string): Promise<PlanSummary | null> {
  return request('GET', `/api/plans/${planId}/summary`, parsePlanSummary, {
    nullOn404: true,
  })
}

export async function generatePlanSummary(planId: string): Promise<PlanSummary> {
  const result = await request(
    'POST',
    `/api/plans/${planId}/summary`,
    parsePlanSummary,
  )
  if (result === null) {
    throw new Error('Plan summary generation failed')
  }
  return result
}

export interface PlanSummaryPdf {
  readonly blob: Blob
  readonly filename: string
}

/**
 * Fetches the server-rendered PDF export of a plan summary. Returns the
 * binary blob plus a suggested filename derived from the backend
 * `Content-Disposition` header (with a sensible fallback when the header
 * is missing). The caller is responsible for triggering the download.
 */
export async function fetchPlanSummaryPdf(
  planId: string,
): Promise<PlanSummaryPdf> {
  const headers = await getAuthHeader()
  const response = await fetch(
    `${AGENT_URL}/api/plans/${planId}/export-pdf`,
    { method: 'GET', headers },
  )
  if (!response.ok) {
    const message = await extractErrorMessage(response)
    throw new Error(`${response.status} ${message}`)
  }
  const blob = await response.blob()
  const filename = parseContentDispositionFilename(
    response.headers.get('content-disposition'),
  )
  return {
    blob,
    filename: filename ?? `plan-summary-${planId}.pdf`,
  }
}

/**
 * Triggers a browser download for a PDF blob by synthesising a one-shot
 * anchor element. Split out so it can be unit-tested in isolation from
 * the fetch path.
 */
export function downloadPdfBlob(pdf: PlanSummaryPdf): void {
  const url = URL.createObjectURL(pdf.blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = pdf.filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export function parseContentDispositionFilename(
  header: string | null,
): string | null {
  if (!header) return null
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
  if (!match) return null
  try {
    return decodeURIComponent(match[1] as string)
  } catch {
    return match[1] ?? null
  }
}
